import clsx from 'clsx'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import { Icon, type IconName } from '../../components/Icon.js'
import { formatTime } from '../../lib/format.js'
import { blockMetaLevel } from './lib/filters.js'
import { durationLabel, minutesBetween } from './lib/geometry.js'
import type { BoardEvent, CalendarEventType } from './lib/types.js'
import { MIN_RESIZABLE_PX, type BlockState } from './VisitBlock.js'

/**
 * Booked time that is not a customer job.
 *
 * Same chassis as a visit — selection ring, resize handles — and a deliberately
 * flatter skin: ink-100 fill, dashed edge, a tighter corner, its type glyph on
 * the top line, and **no shadow at rest**. A job is the lifted, tinted card and
 * a break or a meeting is paper, so a row of them reads as the quiet part of
 * the day at a glance. Only a block travelling under the pointer lifts.
 * Colour-by-category never reaches this. A quarter-hour stop is the glyph
 * alone, because the title will not fit. Decision 67.
 */

const RING = {
  ink900: '#1e2320',
  ink400: '#9ca49f',
  red500: '#ef4444',
} as const

const DRAG_SHADOW = '0 14px 36px rgb(15 23 42 / .28), 0 4px 10px rgb(15 23 42 / .16)'

function ring(colour: string, width: number): string {
  return `inset 0 0 0 ${width}px ${colour}`
}

export const EVENT_TYPE_LABEL: Record<CalendarEventType, string> = {
  TASK: 'Task',
  BREAK: 'Break',
  MEETING: 'Meeting',
  TRAINING: 'Training',
  ADMIN: 'Admin',
  OTHER: 'Booked',
}

export const EVENT_TYPE_ICON: Record<CalendarEventType, IconName> = {
  TASK: 'task',
  BREAK: 'break',
  MEETING: 'people',
  TRAINING: 'people',
  ADMIN: 'card',
  OTHER: 'task',
}

export function eventSuburb(event: BoardEvent): string | null {
  return event.address?.suburb ?? event.address?.city ?? null
}

const ICON_ONLY_PX = 28

export function EventBlock({
  event,
  style,
  state,
  compact = false,
  dense = false,
  short = false,
  showHandles,
  axis = 'y',
  label,
  onPointerDown,
  onResizeStart,
  onOpen,
  onKeyDown,
  registerRef,
}: {
  event: BoardEvent
  style: CSSProperties
  state: BlockState
  compact?: boolean
  /** A compact timeline row: the type and time, then the title, and no third line. */
  dense?: boolean
  short?: boolean
  showHandles: boolean
  axis?: 'x' | 'y'
  label?: string | null
  onPointerDown?: (event: ReactPointerEvent) => void
  onResizeStart?: (event: ReactPointerEvent, edge: 'start' | 'end') => void
  onOpen?: () => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  registerRef?: (element: HTMLElement | null) => void
}) {
  const minutes = minutesBetween(event.startAt, event.endAt)
  const meta = blockMetaLevel(minutes)
  const drawn = axis === 'x' ? Number.parseFloat(String(style.width)) : Number.parseFloat(String(style.height))
  const iconOnly =
    meta === 'nameOnly' ||
    compact ||
    (Number.isFinite(drawn) && drawn > 0 && drawn < ICON_ONLY_PX)
  const completed = Boolean(event.completedAt)
  const suburb = eventSuburb(event)
  const typeLabel = EVENT_TYPE_LABEL[event.type]
  const icon = EVENT_TYPE_ICON[event.type]

  // Nothing at rest: the dashed outline is the whole edge. A picked block earns
  // its 2px ink ring, and only a dragged one is allowed to leave the surface.
  const skin =
    state === 'selected'
      ? ring(RING.ink900, 2)
      : state === 'conflict'
        ? ring(RING.red500, 2)
        : state === 'dragging'
          ? [DRAG_SHADOW, ring(RING.ink900, 2)].join(',')
          : 'none'

  const title = event.title || typeLabel
  const timeText = label ?? formatTime(event.startAt)
  const showMeta = meta !== 'nameOnly' && !compact && !iconOnly
  const third =
    suburb ??
    (event.type === 'MEETING' && event.attendees.length > 1
      ? `${event.attendees.length} people`
      : null)

  return (
    <div
      ref={registerRef}
      role="button"
      tabIndex={0}
      aria-label={`${typeLabel}, ${formatTime(event.startAt)} to ${formatTime(event.endAt)}, ${title}`}
      aria-pressed={state === 'selected'}
      title={[typeLabel, title === typeLabel ? null : title, event.note]
        .filter(Boolean)
        .join(' — ')}
      style={
        {
          ...style,
          '--block-shadow': skin,
          '--block-shadow-hover': state === 'idle' ? ring(RING.ink400, 1) : skin,
        } as CSSProperties
      }
      onPointerDown={onPointerDown}
      onKeyDown={(pointer) => {
        if (pointer.key === 'Enter' || pointer.key === ' ') {
          pointer.preventDefault()
          onOpen?.()
        }
        onKeyDown?.(pointer)
      }}
      onDoubleClick={onOpen}
      className={clsx(
        // A tighter corner than a booking's 10px, which is the other half of
        // reading as flatter beside one.
        'board-block group absolute touch-none rounded-md bg-ink-100 text-left select-none',
        completed ? 'text-ink-400' : 'text-ink-800',
        state === 'dragging'
          ? 'z-30 cursor-grabbing'
          : state === 'selected' || state === 'conflict'
            ? 'z-20 cursor-grab'
            : 'cursor-grab',
        // Dashed edge is the "not a job" tell, and with no shadow under it, it
        // is also the only edge. box-shadow cannot dash, so this is a real
        // outline rather than another inset ring.
        'outline outline-1 outline-dashed outline-ink-300 -outline-offset-1',
      )}
    >
      {iconOnly ? (
        <div className="flex h-full items-center justify-center">
          <Icon
            name={icon}
            className={clsx('size-3', completed && 'opacity-60')}
            strokeWidth={2.25}
          />
        </div>
      ) : (
        <div className={clsx('relative h-full overflow-hidden px-2', dense ? 'py-[3px]' : 'py-1.5')}>
          {showMeta ? (
            // The glyph says what kind of time this is, where a pill spelling
            // "Break" beside a card titled Break said it twice. The word is
            // still on the block's accessible name and its tooltip.
            <p className="flex items-center gap-1.5 truncate text-[10px] leading-tight font-medium tabular-nums text-ink-500">
              <Icon name={icon} className="size-3 text-ink-500" strokeWidth={2.25} />
              <span>{timeText}</span>
              {meta === 'full' && !label ? (
                <span className="font-normal text-ink-400">{durationLabel(minutes)}</span>
              ) : null}
            </p>
          ) : null}

          <p
            className={clsx(
              'truncate text-[12px] font-semibold',
              dense ? 'leading-tight' : 'leading-snug',
              completed && 'line-through',
            )}
          >
            {title}
          </p>

          {third && showMeta && !dense ? (
            <p className="truncate text-[10.5px] leading-snug text-ink-500">{third}</p>
          ) : null}

          {compact && label ? (
            <p className="truncate text-[10px] leading-tight tabular-nums opacity-70">{label}</p>
          ) : null}
        </div>
      )}

      {showHandles ? (
        <>
          <EventResizeHandle
            edge="start"
            axis={axis}
            outside={short}
            visible={state === 'selected'}
            onResizeStart={onResizeStart}
          />
          <EventResizeHandle
            edge="end"
            axis={axis}
            outside={short}
            visible={state === 'selected'}
            onResizeStart={onResizeStart}
          />
        </>
      ) : null}
    </div>
  )
}

export { MIN_RESIZABLE_PX }

export function EventResizeHandle({
  edge,
  axis,
  outside,
  visible,
  onResizeStart,
}: {
  edge: 'start' | 'end'
  axis: 'x' | 'y'
  outside: boolean
  visible: boolean
  onResizeStart?: (event: ReactPointerEvent, edge: 'start' | 'end') => void
}) {
  return (
    <span
      aria-hidden="true"
      onPointerDown={(event) => onResizeStart?.(event, edge)}
      className={clsx(
        'absolute z-10 flex items-center justify-center',
        axis === 'y'
          ? clsx(
              'inset-x-0 cursor-ns-resize',
              outside ? 'h-3' : 'h-2.5',
              edge === 'start' ? (outside ? '-top-3' : 'top-0') : outside ? '-bottom-3' : 'bottom-0',
            )
          : clsx(
              'inset-y-0 cursor-ew-resize',
              outside ? 'w-3' : 'w-2.5',
              edge === 'start' ? (outside ? '-left-3' : 'left-0') : outside ? '-right-3' : 'right-0',
            ),
      )}
    >
      <span
        className={clsx(
          'rounded-full border border-ink-400 bg-white transition-opacity',
          visible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          axis === 'y' ? 'h-1 w-3/5' : 'h-3/5 w-1',
        )}
      />
    </span>
  )
}
